import React, { useRef, useState } from 'react';
import axios from 'axios';
import type { FormikHelpers, FormikValues } from 'formik';
import { Formik } from 'formik';
import { chain, flatMap, isEmpty, isEqual, pickBy, reduce } from 'lodash';
import * as Yup from 'yup';

import { BUSINESS_TAB_KEY } from '~/components/Contacts/UpsertContact/Tabs/Business/constants';
import { FINANCE_TAB_KEY } from '~/components/Contacts/UpsertContact/Tabs/Finance/constants';
import type {
  InitialValuesType,
  SpecialValidationRequirementsType,
  Tab,
} from '~/components/Contacts/UpsertContact/types';
import { useContactTabs } from '~/components/Contacts/UpsertContact/useContactTabs';
import WarningDuplicateContactsDialog from '~/components/Contacts/UpsertContact/WarningDuplicateContactsDialog';
import WarningInProgressPaymentsDialog from '~/components/Contacts/UpsertContact/WarningInProgressPaymentsDialog';
import { PERMISSION_VERBS } from '~/components/core';
import { useCms } from '~/components/hooks/useCms';
import { useSanitizeByPermissions } from '~/components/hooks/useHasPermission';
import type { ContactFullModel, Finance } from '~/server_shared/swagger/data-contracts';
import { PAYMENT_METHODS_DETAILS_DICT } from '~/Types';
import { reportAxiosError } from '~/Utils';

// Tabs that should still contribute to the payload when hidden, so that cleared
// values persist to the server (e.g. business fields after a country change).
const INCLUDE_IN_PAYLOAD_WHEN_HIDDEN = [BUSINESS_TAB_KEY];

// All contact field names used for bank details (computed once from payment method configs)
const BANK_DETAIL_FIELD_NAMES: string[] = Array.from(
  Object.values(PAYMENT_METHODS_DETAILS_DICT).reduce((fields, method) => {
    const contactBankFields = (method as { contact_bank_fields?: Record<string, string> }).contact_bank_fields;
    if (contactBankFields) {
      Object.keys(contactBankFields).forEach((field) => fields.add(field));
    }
    return fields;
  }, new Set<string>())
);

interface UpsertContactFormikPropType {
  contact?: ContactFullModel;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
  onPreparePayload?: (payload: Record<string, unknown>) => Record<string, unknown>;
  specialValidationRequirements?: SpecialValidationRequirementsType;
  initialValues?: InitialValuesType;
  dialogInnerComponent: React.ReactElement;
}

const UpsertContactFormik: React.FC<UpsertContactFormikPropType> = ({
  contact: existingContact,
  onClose,
  onSubmit,
  onPreparePayload,
  specialValidationRequirements = {},
  initialValues,
  dialogInnerComponent,
}) => {
  const { userOrganization } = useCms();
  const { allTabs, filteredTabs } = useContactTabs();
  const [duplicateData, setDuplicateData] = useState(null);
  const [ignoreDuplicates, setIgnoreDuplicates] = useState(false);
  const [inProgressPaymentsData, setInProgressPaymentsData] = useState<{
    finances: Finance[];
  } | null>(null);
  // Using ref to avoid stale closure issue when submitForm() is called after setState
  const skipInProgressPaymentsCheckRef = useRef(false);
  const isEditing = !!existingContact;

  const sanitizePayload = useSanitizeByPermissions({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fieldsPermissionActions: reduce(
      allTabs,
      (result, tab) => ({
        ...result,
        ...(tab.fieldsPermissionActions || {}),
      }),
      {}
    ),
    verb: PERMISSION_VERBS.WRITE,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    forbiddenFields: flatMap(allTabs, (tab) => tab.forbiddenFields || []),
  });

  const getInitialValues = () => {
    return chain(allTabs)
      .keyBy('tabKey')
      .mapValues((tab) => tab.getInitialValues(existingContact, initialValues))
      .value();
  };

  const getValidationSchema = () => {
    const validations = chain(allTabs)
      .keyBy('tabKey')
      .mapValues((tab) => Yup.object().shape(tab.getValidationSchema(specialValidationRequirements)))
      .value();

    return Yup.object().shape(validations);
  };

  const wereTabValuesUnchanged = (tab: Tab, values1: FormikValues, values2: FormikValues) => {
    const formattedValues1 = tab.getValuesToCompare(values1);
    const formattedValues2 = tab.getValuesToCompare(values2);

    return isEqual(formattedValues1, formattedValues2);
  };

  const prepareTabPayload = async (tab: Tab, values: FormikValues) => {
    const initialTabValues = tab.getInitialValues(existingContact);
    if (isEditing && wereTabValuesUnchanged(tab, initialTabValues, values[tab.tabKey])) {
      return {};
    }

    if (tab.getPreparedPayload) {
      return await tab.getPreparedPayload(values[tab.tabKey]);
    } else {
      if (isEditing) {
        return pickBy(values[tab.tabKey], (value, key) => !isEqual(value, initialTabValues[key]));
      } else {
        return values[tab.tabKey];
      }
    }
  };

  const preparePayload = async (filteredTabs: Tab[], values: FormikValues) => {
    // Some tabs can become hidden mid-session (e.g. business tab after a country
    // change away from AU/NZ).  Their fields are cleared by the change handler,
    // but filteredTabs excludes hidden tabs so the cleared values never reach the
    // server.  Include these tabs when their values were modified during the session.
    const hiddenTabsToInclude = isEditing
      ? allTabs.filter((tab) => INCLUDE_IN_PAYLOAD_WHEN_HIDDEN.includes(tab.tabKey) && !filteredTabs.includes(tab))
      : [];
    const tabsToProcess = [...filteredTabs, ...hiddenTabsToInclude];

    const payloadPromises = tabsToProcess.map(async (tab) => await prepareTabPayload(tab, values));
    const tabPayloads = await Promise.all(payloadPromises);
    const tabs = tabsToProcess.map((tab, index) => ({ tabKey: tab.tabKey, payload: tabPayloads[index] }));

    const payload = chain(tabs)
      .keyBy('tabKey')
      .mapValues('payload')
      .pickBy((tabPayload) => !isEmpty(tabPayload))
      .value();

    const sanitizedPayload = sanitizePayload(payload);

    if (onPreparePayload) {
      return onPreparePayload(sanitizedPayload);
    }
    return sanitizedPayload;
  };

  const checkDuplicates = async (tabs: Tab[], values: FormikValues) => {
    if (ignoreDuplicates) {
      return { has_duplicates: false };
    }

    const params = reduce(
      tabs,
      (result, tab) => ({
        ...result,
        ...(tab.getDuplicateCheckParams ? tab.getDuplicateCheckParams(values) : {}),
      }),
      {}
    );

    const response = await axios.get(`/api/v1/organizations/${userOrganization.id}/contacts/duplicates`, { params });

    return response.data;
  };

  const hasFinanceBankDetailChanges = (payload: Record<string, unknown>): boolean => {
    const financePayload = payload[FINANCE_TAB_KEY] as Record<string, unknown> | undefined;
    if (!financePayload) return false;

    return BANK_DETAIL_FIELD_NAMES.some((field) => field in financePayload);
  };

  const checkInProgressPayments = async (contactId: number) => {
    if (skipInProgressPaymentsCheckRef.current) {
      return { has_in_progress_payments: false };
    }

    const response = await axios.get(`/api/v1/contacts/${contactId}/finance/check_in_progress_payments`);
    return response.data;
  };

  const handleSubmit = async (values: FormikValues, { setSubmitting }: FormikHelpers<FormikValues>) => {
    try {
      const duplicateResponse = await checkDuplicates(filteredTabs, values);
      if (duplicateResponse.has_duplicates) {
        setDuplicateData(duplicateResponse);
        return;
      }

      const payload = await preparePayload(filteredTabs, values);

      // Check for in-progress payments if editing contact and bank details are changing
      if (isEditing && existingContact && hasFinanceBankDetailChanges(payload)) {
        const inProgressResponse = await checkInProgressPayments(existingContact.id);
        if (inProgressResponse.has_in_progress_payments) {
          setInProgressPaymentsData(inProgressResponse);
          return;
        }
      }

      await onSubmit(payload);
      onClose();
    } catch (error) {
      setSubmitting(false);
      await reportAxiosError(error);
    } finally {
      skipInProgressPaymentsCheckRef.current = false;
    }
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={getValidationSchema()}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ submitForm, isSubmitting }) => {
        const submitFormIgnoreDups = async () => {
          setIgnoreDuplicates(true);
          await submitForm();
        };

        const submitFormIgnorePayments = async () => {
          skipInProgressPaymentsCheckRef.current = true;
          await submitForm();
        };

        return (
          <>
            {dialogInnerComponent}
            {duplicateData ? (
              <WarningDuplicateContactsDialog
                duplicates={duplicateData}
                onCreateDuplicate={submitFormIgnoreDups}
                onCancel={() => setDuplicateData(null)}
                disabled={isSubmitting}
              />
            ) : null}
            {inProgressPaymentsData ? (
              <WarningInProgressPaymentsDialog
                finances={inProgressPaymentsData.finances}
                onProceed={submitFormIgnorePayments}
                onCancel={() => setInProgressPaymentsData(null)}
                disabled={isSubmitting}
              />
            ) : null}
          </>
        );
      }}
    </Formik>
  );
};

export default UpsertContactFormik;
